import { Percent, RouteStatus } from '@emberswap/emberswap-sdk2/dist/entities'
import { Disclosure, Transition } from '@headlessui/react'
import React, { FC, useMemo, useState } from 'react'

import { INITIAL_ALLOWED_SLIPPAGE } from '../../constants'
import { classNames } from '../../functions'
import { warningSeverity } from '../../functions/trade/warningSeverity'
import { useSettings } from '../../state/user/hooks'
import { ChevronDownIcon,InformationCircleIcon } from '../Icons'
import { Rate } from '../Rate'
import { Route } from '../Route'
import { Tooltip } from '../Tooltip'
import { useTrade } from '../TradeProvider'

export interface SwapStatsProps {
  chainId: number
}

export const SwapStatsDisclosure: FC<SwapStatsProps> = ({ chainId, }) => {
  const { trade } = useTrade()
  const [showRoute, setShowRoute] = useState(false)

  const [{ userSlippageTolerance, slippageToleranceType }] = useSettings()

    const swapSlippage = useMemo(() => {
      if (userSlippageTolerance && slippageToleranceType !== 'auto') {
        return new Percent(Number(userSlippageTolerance) * 100, 10_000)
      } else return new Percent(Number(INITIAL_ALLOWED_SLIPPAGE) * 100, 10_000) // 0.5%
    }, [slippageToleranceType, userSlippageTolerance])

  const priceImpactSeverity = useMemo(
    () => warningSeverity(trade?.priceImpact),
    [trade?.priceImpact]
  )

  const stats = (
    <>
      <p className="text-sm text-slate-700">Price Impact</p>
      <p
        className={classNames(
          priceImpactSeverity === 2
            ? 'text-yellow-500'
            : priceImpactSeverity > 2
            ? 'text-red-600'
            : 'text-slate-600',
          'text-right truncate text-sm font-medium'
        )}
      >
        {trade?.priceImpact?.multiply(-1).toFixed(2)}%
      </p>
      <div className="col-span-2 border-t border-slate-200/5 w-full py-0.5" />
      <p className="text-sm text-slate-700">Min. Received</p>
      <p className="text-sm font-medium text-right truncate text-slate-600">
        {trade?.minimumAmountOut(swapSlippage)?.toSignificant(6)}{' '}
        {trade?.minimumAmountOut(swapSlippage)?.currency.symbol}
      </p>
      <p className="text-sm text-slate-700">Optimized Route</p>
      <p
        onClick={() =>{
          trade?.route.status !== RouteStatus.NoWay && setShowRoute((prev) => !prev)}}
        className={classNames(trade?.route.status == RouteStatus.NoWay ? 'cursor-not-allowed' : 'cursor-pointer', " text-sm font-medium text-right text-slate-800 hover:text-black")}
      >
        {showRoute ? 'Hide' : 'Show'}
      </p>
      <Route
        chainId={chainId}

        trade={trade}
        open={showRoute}
        setOpen={setShowRoute}
      />
    </>
  )

  return (
    <>
      <Transition
        show={!!trade}
        unmount={false}
        className="p-3 !pb-1 transition-[max-height] overflow-hidden"
        enter="duration-300 ease-in-out"
        enterFrom="transform max-h-0"
        enterTo="transform max-h-[380px]"
        leave="transition-[max-height] duration-250 ease-in-out"
        leaveFrom="transform max-h-[380px]"
        leaveTo="transform max-h-0"
      >
        <Disclosure>
          {({ open }) => (
            <>
              <div className="flex justify-between items-center bg-white bg-opacity-[0.04] hover:bg-opacity-[0.08] rounded-2xl px-4 mb-4 py-2.5 gap-2">
                <Rate price={trade?.executionPrice}>
                  {({ content, usdPrice, toggleInvert }) => (
                    <div
                      className="text-sm text-slate-600 hover:text-slate-500 cursor-pointer flex items-center gap-1 font-semibold tracking-tight h-[36px] truncate"
                      onClick={toggleInvert}
                    >
                      <Tooltip
                        panel={<div className="grid grid-cols-2 gap-1">{stats}</div>}
                        button={<InformationCircleIcon width={16} height={16} />}
                      />{' '}
                      {content}{' '}
                      {usdPrice && (
                        <span className="font-medium text-slate-500">(${usdPrice})</span>
                      )}
                    </div>
                  )}
                </Rate>
                <Disclosure.Button className="flex items-center justify-end flex-grow cursor-pointer">
                  <ChevronDownIcon
                    width={24}
                    height={24}
                    className={classNames(
                      open ? '!rotate-180' : '',
                      'rotate-0 transition-[transform] duration-300 ease-in-out delay-200'
                    )}
                  />
                </Disclosure.Button>
              </div>
              <Transition
                show={open}
                unmount={false}
                className="transition-[max-height] overflow-hidden"
                enter="duration-300 ease-in-out"
                enterFrom="transform max-h-0"
                enterTo="transform max-h-[380px]"
                leave="transition-[max-height] duration-250 ease-in-out"
                leaveFrom="transform max-h-[380px]"
                leaveTo="transform max-h-0"
              >
                <Disclosure.Panel
                  as="div"
                  className="grid grid-cols-2 gap-1 px-4 py-2 mb-4 border border-slate-200/5 rounded-2xl"
                >
                  {stats}
                </Disclosure.Panel>
              </Transition>
            </>
          )}
        </Disclosure>
      </Transition>
    </>
  )
}
